import React, { createContext, memo, useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';

export const RootNodeRenderer = memo(
	({ children, maxRenderIndex, batchSize, onBatchRendered }: RootNodeRendererProps) => {
		const [batchIndex, setBatchIndex] = useState(0);

		const lastBatchIndex = Math.max(Math.ceil(maxRenderIndex / batchSize), 1) - 1; // The last valid batch index

		useEffect(() => {
			// eslint-disable-next-line compat/compat
			const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
			const cancelHandle = requestIdleCallback(() => {
				const done = batchIndex >= lastBatchIndex;

				onBatchRendered?.(done);

				if (!done) {
					setBatchIndex((currentBatchIndex) =>
						currentBatchIndex < lastBatchIndex ? currentBatchIndex + 1 : currentBatchIndex,
					);
				}
			});

			return () => {
				const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
				cancelIdleCallback(cancelHandle);
			};
		}, [batchIndex, lastBatchIndex, onBatchRendered, setBatchIndex]);

		const maxBatchRenderIndex = (batchIndex + 1) * batchSize;

		return (
			<NodeRendererContext.Provider value={maxBatchRenderIndex}>
				{children}
			</NodeRendererContext.Provider>
		);
	},
);

type RootNodeRendererProps = PropsWithChildren<{
	batchSize: number;
	maxRenderIndex: number;
	onBatchRendered?: (done: boolean) => void;
}>;

export const NodeRendererContext = createContext<number>(0);
